import rewind from '@mapbox/geojson-rewind';
import { geoMercator, geoPath } from 'd3-geo';
import gjvv from 'geojson-validation';

export interface IGeoProps {
  geojson: any;
  fill?: string;
  stroke?: string;
  /** @deprecated Kept for backward compatibility; the stroke width is now fixed. */
  weight?: string;
  width?: string;
  height?: string;
  extraStyles?: object;
}

/**
 * Fixed internal viewport. d3-geo needs numbers to fit a projection into, while
 * consumers pass CSS strings for width/height ("20px", "100%"). Projecting into
 * a square viewBox and letting the SVG scale bridges the two.
 */
const VIEWPORT = 100;

export const GeoJsonToSvg = (props: IGeoProps) => {
  const {
    geojson,
    fill = '#C2C7D0',
    stroke = '#C2C7D0',
    width = '20px',
    height = '20px',
    extraStyles
  } = props;

  const fallback = <div style={{ width, height, backgroundColor: stroke }} />;

  if (!geojson) {
    return fallback;
  }

  // Consumers pass field/version objects that carry `.geometry` but no `type`,
  // so the GeoJSON type has to be supplied: both geojson-rewind and d3-geo
  // dispatch on it and silently render nothing when it is missing.
  const feature =
    'geometry' in geojson
      ? { properties: {}, ...geojson, type: 'Feature' }
      : { type: 'Feature', geometry: geojson, properties: {} };

  let path: string | null = null;

  try {
    if (!gjvv.valid(feature)) {
      return fallback;
    }

    // `rewind` normalises ring winding order, which is what makes holes render
    // as holes under d3-geo's non-zero fill rule.
    const rewinded = rewind(feature, true);
    const projection = geoMercator().fitSize([VIEWPORT, VIEWPORT], rewinded);

    path = geoPath().projection(projection)(rewinded);
  } catch {
    return fallback;
  }

  // Degenerate geometries (a zero-area ring, a bare point) project to an
  // infinite scale, which surfaces as NaN in the path data.
  if (!path || path.includes('NaN')) {
    return fallback;
  }

  return (
    <svg
      viewBox={`0 0 ${VIEWPORT} ${VIEWPORT}`}
      width={width}
      height={height}
      style={{ ...extraStyles }}
    >
      <path d={path} fill={fill} stroke={stroke} strokeWidth={1} />
    </svg>
  );
};
